// =====================================================================
// Commissioning schemas (UI redesign Phase C/D)
// =====================================================================
// One document per project (Firestore: workspaces/{wid}/projects/{pid}/
// meta/commissioning) holding Setup-wizard step progress plus per-device
// test results. Results are recorded from three surfaces (wizard Test
// step, Topology side panel, device-detail Test tab) and upsert on
// device_id+kind+target.
// =====================================================================

import { z } from 'zod';

export const WizardStepSchema = z.enum([
  'connect',
  'discover',
  'adopt',
  'verify',
  'test',
  'signoff',
]);
export type WizardStep = z.infer<typeof WizardStepSchema>;

export const StepStateSchema = z.enum(['pending', 'in_progress', 'done']);
export type StepState = z.infer<typeof StepStateSchema>;

export const DeviceTestResultSchema = z.object({
  device_id: z.string().min(1),
  kind: z.enum(['relay_channel', 'input_channel', 'stepper_axis', 'sensor_reading', 'drive_board']),
  /** Channel id / axis index / sensor id the result applies to. */
  target: z.string().min(1),
  status: z.enum(['pass', 'fail', 'skipped']),
  tested_at_ms: z.number().int(),
  tested_by_uid: z.string().min(1),
  note: z.string().nullable(),
});
export type DeviceTestResult = z.infer<typeof DeviceTestResultSchema>;

export const CommissioningStepsSchema = z.object({
  connect: StepStateSchema,
  discover: StepStateSchema,
  adopt: StepStateSchema,
  verify: StepStateSchema,
  test: StepStateSchema,
  signoff: StepStateSchema,
});
export type CommissioningSteps = z.infer<typeof CommissioningStepsSchema>;

export const CommissioningDocSchema = z.object({
  project_id: z.string().min(1),
  current_step: WizardStepSchema,
  steps: CommissioningStepsSchema,
  results: z.array(DeviceTestResultSchema),
  /** Manual wiring-checklist state for the Verify step (key → ticked). */
  verify_checklist: z.record(z.string(), z.boolean()).default({}),
  updated_at_ms: z.number().int(),
  signed_off_at_ms: z.number().int().nullable(),
  signed_off_by_uid: z.string().nullable(),
});
export type CommissioningDoc = z.infer<typeof CommissioningDocSchema>;

/** Fresh default doc — all steps pending, wizard at Connect. */
export function defaultCommissioningDoc(projectId: string, nowMs: number): CommissioningDoc {
  return {
    project_id: projectId,
    current_step: 'connect',
    steps: {
      connect: 'pending',
      discover: 'pending',
      adopt: 'pending',
      verify: 'pending',
      test: 'pending',
      signoff: 'pending',
    },
    results: [],
    verify_checklist: {},
    updated_at_ms: nowMs,
    signed_off_at_ms: null,
    signed_off_by_uid: null,
  };
}
